import { Check, Loader2, PhoneOff } from "lucide-react";
import { cn } from "@/utils";

export interface CasePhoneNumbersView {
  payer_phone_number: string;
  available_phone_numbers: string[];
  tried_phone_numbers: string[];
  all_numbers_exhausted: boolean;
  alternate_contact_number?: string | null;
  /** From intake rows: call finished with a live PA outcome on the active dial. */
  success?: string;
  fail_class?: string;
  /** Phone list was narrowed to numbers validated against Stedi eligibility. */
  stedi_payer_phones_filtered?: boolean;
  stedi_payer_phone_source?: string | null;
  /** Stedi returned phone(s) that are not in payerPhoneDirectory. */
  stedi_payer_phone_directory_mismatch?: boolean;
  stedi_returned_phone_numbers?: string[];
  payer_directory_phone_numbers?: string[];
  /** BCBS state-aware routing: home-plan route a generic BCBS case dialed on. */
  bcbs_routed_state?: string | null;
}

/**
 * Informational chip for Federal (FEP) BCBS members. We keep dialing the given
 * national FEP line, but flag that state-specific Federal alternates exist on
 * file. A Federal member's administering state can't be auto-detected from the
 * Stedi hit, so these are never auto-dialed — the chip just tells ops a state
 * FEP line may be worth looking up if the national number stalls.
 */
function BcbsFederalAlternatesChip({ view }: { view: CasePhoneNumbersView }) {
  if (view.bcbs_routed_state !== "federal") return null;
  return (
    <span
      className="inline-flex items-center rounded-sm bg-indigo-100 px-1.5 py-0.5 text-[10px] font-medium text-indigo-900 dark:bg-indigo-950/60 dark:text-indigo-200"
      title="Federal (FEP) member on the national line. State-specific Federal alternates are on file."
    >
      Other FEP lines available
    </span>
  );
}

function isActiveDialSettled(view: CasePhoneNumbersView): boolean {
  if (view.success === "true") return true;
  if (view.fail_class === "live_outcome") return true;
  return false;
}

/** True when this number has finished its per-number retry cycle (exhausted or rotated past). */
export function isPhoneRetryCycleComplete(
  phone: string,
  view: CasePhoneNumbersView,
  triedSet: Set<string>,
): boolean {
  if (view.all_numbers_exhausted && phone === view.payer_phone_number) {
    return true;
  }
  // Successful calls keep payer_phone_number on the last dialed number; no tried list update.
  if (phone === view.payer_phone_number && isActiveDialSettled(view)) {
    return true;
  }
  if (triedSet.has(phone)) {
    return true;
  }
  return false;
}

function PhoneNumberList({
  label,
  numbers,
  tone = "default",
}: {
  label: string;
  numbers: string[];
  tone?: "default" | "muted";
}) {
  return (
    <div className="space-y-0.5">
      <div className="text-[10px] font-medium text-muted-foreground">{label}</div>
      {numbers.length > 0 ? (
        <ul className="space-y-0.5 font-mono text-[11px]">
          {numbers.map((phone) => (
            <li key={`${label}-${phone}`}>{phone}</li>
          ))}
        </ul>
      ) : (
        <div
          className={cn(
            "text-[11px] italic",
            tone === "muted" ? "text-muted-foreground" : "text-amber-700",
          )}
        >
          none
        </div>
      )}
    </div>
  );
}

function StediDirectoryMismatchBanner({ view }: { view: CasePhoneNumbersView }) {
  const stediNumbers = view.stedi_returned_phone_numbers ?? [];
  const directoryNumbers = view.payer_directory_phone_numbers ?? [];

  return (
    <div className="space-y-2 rounded-sm border border-amber-200 bg-amber-50/80 p-2 dark:border-amber-900/60 dark:bg-amber-950/30">
      <span
        className="inline-flex items-center rounded-sm bg-amber-100 px-1.5 py-0.5 text-[10px] font-medium text-amber-900 dark:bg-amber-950/60 dark:text-amber-200"
        title="Stedi eligibility returned phone(s) that are not in payerPhoneDirectory."
      >
        Stedi / directory mismatch
      </span>
      <PhoneNumberList label="Stedi returned" numbers={stediNumbers} />
      <PhoneNumberList label="Directory available" numbers={directoryNumbers} tone="muted" />
    </div>
  );
}

export function CasePhoneNumbersTable({
  view,
  onSelectPhone,
  selectedPhone,
}: {
  view: CasePhoneNumbersView;
  onSelectPhone?: (phone: string) => void;
  selectedPhone?: string;
}) {
  const numbers =
    view.available_phone_numbers.length > 0
      ? view.available_phone_numbers
      : view.payer_phone_number
        ? [view.payer_phone_number]
        : [];

  const triedSet = new Set(view.tried_phone_numbers);
  const extraAlternate =
    view.alternate_contact_number &&
    !numbers.includes(view.alternate_contact_number)
      ? view.alternate_contact_number
      : null;

  const stediSourceLabel =
    view.stedi_payer_phone_source === "benefits_related_entity"
      ? "benefits related entity"
      : view.stedi_payer_phone_source === "payer"
        ? "payer block"
        : view.stedi_payer_phone_source?.replace(/_/g, " ");

  const phoneTable =
    numbers.length === 0 && !extraAlternate ? (
      <span className="inline-flex items-center gap-1 text-amber-700">
        <PhoneOff className="h-3 w-3" />
        none
      </span>
    ) : (
      (() => {
        const rows = extraAlternate ? [...numbers, extraAlternate] : numbers;
        const clickable = Boolean(onSelectPhone);
        return (
          <table className="w-full min-w-[140px] border-collapse text-[11px]">
            <tbody>
              {rows.map((phone) => {
                const isActiveDial = phone === view.payer_phone_number;
                const isSelected = selectedPhone === phone;
                const isGeminiAlt =
                  phone === view.alternate_contact_number &&
                  !numbers.includes(phone);
                const cycleComplete = isPhoneRetryCycleComplete(phone, view, triedSet);
                const showDialSpinner = isActiveDial && !cycleComplete;
                return (
                  <tr
                    key={phone}
                    className={cn(
                      isActiveDial ? "text-foreground" : "text-muted-foreground",
                      isSelected && "bg-primary/5",
                    )}
                  >
                    <td className="w-4 pr-1 align-top pt-0.5">
                      {cycleComplete ? (
                        <Check
                          className="h-3 w-3 text-green-600 dark:text-green-500"
                          aria-label="Retry cycle complete"
                        />
                      ) : (
                        <span className="inline-block h-3 w-3" aria-hidden />
                      )}
                    </td>
                    <td className="font-mono align-top">
                      {clickable ? (
                        <button
                          type="button"
                          className={cn(
                            "inline-flex items-center gap-1 rounded px-0.5 text-left hover:underline",
                            isSelected && "font-medium text-primary",
                          )}
                          title="Use as dial number for retry"
                          onClick={() => onSelectPhone?.(phone)}
                        >
                          {phone}
                          {showDialSpinner ? (
                            <Loader2
                              className="h-3 w-3 shrink-0 animate-spin text-primary"
                              aria-label="Dialing"
                            />
                          ) : null}
                        </button>
                      ) : (
                        <span className="inline-flex items-center gap-1">
                          {phone}
                          {showDialSpinner ? (
                            <Loader2
                              className="h-3 w-3 shrink-0 animate-spin text-primary"
                              aria-label="Dialing"
                            />
                          ) : null}
                        </span>
                      )}
                      {isGeminiAlt ? (
                        <span
                          className="ml-1 font-sans text-[10px] text-muted-foreground"
                          title="Alternate captured by Gemini"
                        >
                          alt
                        </span>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
      })()
    );

  return (
    <div className="space-y-1">
      {view.stedi_payer_phone_directory_mismatch ? (
        <StediDirectoryMismatchBanner view={view} />
      ) : null}
      {view.stedi_payer_phones_filtered ? (
        <span
          className="inline-flex items-center rounded-sm bg-sky-100 px-1.5 py-0.5 text-[10px] font-medium text-sky-900 dark:bg-sky-950/60 dark:text-sky-200"
          title={
            stediSourceLabel
              ? `Phone list filtered to Stedi-validated directory numbers (${stediSourceLabel})`
              : "Phone list filtered to Stedi-validated directory numbers"
          }
        >
          Stedi filtered
        </span>
      ) : null}
      {phoneTable}
      <BcbsFederalAlternatesChip view={view} />
    </div>
  );
}

/** Parse operator-edited phone list (one per line or comma-separated). */
export function parsePhoneListInput(raw: string): string[] {
  return Array.from(
    new Set(
      raw
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean),
    ),
  );
}

export function formatPhoneListInput(numbers: string[]): string {
  return numbers.join("\n");
}
